import React, { useState } from "react";
import { Box, IconButton } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";

const slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? "100%" : "-100%",
    opacity: 0.6,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    x: direction > 0 ? "-100%" : "100%",
    opacity: 0.6,
  }),
};

const ImageSlider = ({ images }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const handleNextImage = () => {
    if (images.length <= 1) return;
    setDirection(1);
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const handlePrevImage = () => {
    if (images.length <= 1) return;
    setDirection(-1);
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const showArrows = images.length > 1;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: { xs: 280, md: 360 },
        borderRadius: "4px",
        overflow: "hidden",
        boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
        border: "1px solid rgba(255,255,255,0.12)",
        backgroundColor: "rgba(0,0,0,0.2)",
      }}
    >
      <AnimatePresence initial={false} custom={direction}>
        <motion.img
          key={currentImageIndex}
          src={images[currentImageIndex]?.url}
          alt={`Home ${currentImageIndex + 1}`}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            x: { type: "spring", stiffness: 300, damping: 30 },
            opacity: { duration: 0.2 },
          }}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        />
      </AnimatePresence>

      {showArrows && (
        <>
          <IconButton
            onClick={handlePrevImage}
            aria-label="Previous image"
            sx={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 2,
              width: 44,
              height: 44,
              backgroundColor: "rgba(0,29,61,0.9)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.2)",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.15)",
                borderColor: "rgba(255,255,255,0.4)",
              },
            }}
          >
            <ChevronLeft sx={{ fontSize: 28 }} />
          </IconButton>

          <IconButton
            onClick={handleNextImage}
            aria-label="Next image"
            sx={{
              position: "absolute",
              right: 12,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 2,
              width: 44,
              height: 44,
              backgroundColor: "rgba(0,29,61,0.9)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,0.2)",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.15)",
                borderColor: "rgba(255,255,255,0.4)",
              },
            }}
          >
            <ChevronRight sx={{ fontSize: 28 }} />
          </IconButton>
        </>
      )}

      <Box
        sx={{
          position: "absolute",
          bottom: 12,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          gap: 1,
          zIndex: 2,
        }}
      >
        {images.map((_, index) => (
          <Box
            key={index}
            onClick={() => {
              if (images.length <= 1) return;
              setDirection(index > currentImageIndex ? 1 : -1);
              setCurrentImageIndex(index);
            }}
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              cursor: images.length > 1 ? "pointer" : "default",
              backgroundColor:
                index === currentImageIndex
                  ? "#fff"
                  : "rgba(255, 255, 255, 0.4)",
              transition: "background-color 0.2s",
              "&:hover":
                images.length > 1
                  ? {
                      backgroundColor:
                        index === currentImageIndex
                          ? "#fff"
                          : "rgba(255, 255, 255, 0.7)",
                    }
                  : {},
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default ImageSlider;